import { createConsumer, type Subscription } from '@rails/actioncable';
import type { AxiosInstance } from 'axios';
import { type RefObject, useEffect, useRef } from 'react';
import apiService from 'shared/service/api.service';

import { fetchCableTicket } from './cable-ticket';
import { useAxiosInstance } from './utils';

export const RECONNECT_DELAY = 5_000;

type ChannelHandlers = {
  connected: () => void;
  received: (message: unknown) => void;
};

function cableUrl(ticket: string) {
  return `${apiService.baseUrl.replace(/^http/, 'ws')}/cable?ticket=${encodeURIComponent(ticket)}`;
}

let consumer: ReturnType<typeof createConsumer> | undefined;
let ticket = '';
let ticketRequest: Promise<void> | undefined;
let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
let subscriberCount = 0;

type AxiosInstanceRef = RefObject<Promise<AxiosInstance>>;

async function fetchTicket(axiosInstanceRef: AxiosInstanceRef) {
  ticket = await fetchCableTicket(axiosInstanceRef.current);
}

async function reconnect(axiosInstanceRef: AxiosInstanceRef) {
  reconnectTimer = undefined;
  try {
    await fetchTicket(axiosInstanceRef);
  } catch {
    scheduleReconnect(axiosInstanceRef);
    return;
  }
  consumer?.connect();
}

function scheduleReconnect(axiosInstanceRef: AxiosInstanceRef) {
  if (reconnectTimer !== undefined || consumer === undefined) return;

  reconnectTimer = setTimeout(() => reconnect(axiosInstanceRef), RECONNECT_DELAY);
}

function leaveSharedConsumer() {
  subscriberCount -= 1;
  if (subscriberCount > 0) return;

  clearTimeout(reconnectTimer);
  reconnectTimer = undefined;
  consumer?.disconnect();
  consumer = undefined;
  ticketRequest = undefined;
}

export function useCableSubscription(channel: string, handlers: ChannelHandlers) {
  const axiosInstance = useAxiosInstance();
  const axiosInstanceRef = useRef(axiosInstance);
  const handlersRef = useRef(handlers);

  useEffect(() => {
    axiosInstanceRef.current = axiosInstance;
    handlersRef.current = handlers;
  });

  useEffect(() => {
    let subscription: Subscription | undefined;
    let ticketRequestRetryTimer: ReturnType<typeof setTimeout> | undefined;
    let hasLeft = false;
    subscriberCount += 1;

    const subscribe = async () => {
      ticketRequest ??= fetchTicket(axiosInstanceRef);
      try {
        await ticketRequest;
      } catch {
        ticketRequest = undefined;
        if (!hasLeft) ticketRequestRetryTimer = setTimeout(subscribe, RECONNECT_DELAY);
        return;
      }
      if (hasLeft) return;

      consumer ??= createConsumer(() => cableUrl(ticket));
      subscription = consumer.subscriptions.create(channel, {
        connected: () => handlersRef.current.connected(),
        received: (message: unknown) => handlersRef.current.received(message),
        disconnected: ({ willAttemptReconnect }: { willAttemptReconnect: boolean }) => {
          if (!willAttemptReconnect) scheduleReconnect(axiosInstanceRef);
        },
      });
    };

    subscribe();

    return () => {
      hasLeft = true;
      clearTimeout(ticketRequestRetryTimer);
      subscription?.unsubscribe();
      leaveSharedConsumer();
    };
  }, [channel]);
}
